import { useTranslation } from 'react-i18next';
import { AssetReviewItem } from '../../../api';
import type { ResourceReviewItem } from '../../../types';
import { tosThumbnail } from '../../../utils/tosImage';

interface ReviewStatusPanelProps {
  assetReviews: AssetReviewItem[];
  assetReviewRequired: boolean;
  assetReviewAllActive: boolean;
  resourceReviews: ResourceReviewItem[];
  resourceReviewRequired: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSubmitResource: () => void;
  submittingResource?: boolean;
}

/**
 * 合并的审核状态面板：上半部分人物审核（卡片网格），下半部分素材审核（表格 + 提交审核）。
 * 共用一个可折叠外框，两边全部通过时整体收起。
 * 素材审核保留「提交审核」按钮与「未提交」状态（资源审核 submit-flow）。
 */
export default function ReviewStatusPanel({
  assetReviews,
  assetReviewRequired,
  assetReviewAllActive,
  resourceReviews,
  resourceReviewRequired,
  collapsed,
  onToggleCollapsed,
  onSubmitResource,
  submittingResource,
}: ReviewStatusPanelProps) {
  const { t } = useTranslation('episodeDetail');
  const { t: tc } = useTranslation('common');

  const showAsset = assetReviewRequired && assetReviews.length > 0;
  const showResource = resourceReviewRequired && resourceReviews.length > 0;
  if (!showAsset && !showResource) return null;

  const assetOk = !showAsset || assetReviewAllActive;
  const resourceOk = !showResource || resourceReviews.every(r => r.status === 'active');
  const allActive = assetOk && resourceOk;
  const totalCount = (showAsset ? assetReviews.length : 0) + (showResource ? resourceReviews.length : 0);

  const resourceActiveCount = resourceReviews.filter(r => r.status === 'active').length;
  const resourceProcessingCount = resourceReviews.filter(r => r.status === 'processing').length;
  const resourceFailedCount = resourceReviews.filter(r => r.status === 'failed').length;
  const resourceUnsubmittedCount = resourceReviews.filter(r => r.status === 'unsubmitted').length;

  const resourceStatusBadge = (status: string, errorMsg?: string) => {
    switch (status) {
      case 'active':
        return <span style={{ color: '#52c41a', fontSize: '0.82rem' }}>{tc('resourceReview.statusApproved')}</span>;
      case 'processing':
        return <span style={{ color: '#faad14', fontSize: '0.82rem' }}>{tc('resourceReview.statusProcessing')}</span>;
      case 'failed':
        return (
          <span style={{ color: '#ff4d4f', fontSize: '0.82rem' }} title={errorMsg}>
            {errorMsg ? tc('resourceReview.statusFailedWithMsg', { msg: errorMsg }) : tc('resourceReview.statusFailed')}
          </span>
        );
      case 'unsubmitted':
        return <span style={{ color: 'var(--text-3)', fontSize: '0.82rem' }}>{tc('resourceReview.statusUnsubmitted')}</span>;
      default:
        return <span style={{ fontSize: '0.82rem', color: 'var(--text-3)' }}>{status}</span>;
    }
  };

  const resourceTypeLabel = (type: string) => {
    switch (type) {
      case 'image': return tc('resourceReview.typeImage');
      case 'video': return tc('resourceReview.typeVideo');
      case 'audio': return tc('resourceReview.typeAudio');
      default: return type;
    }
  };

  const resourceTypeIcon = (type: string) => {
    const common = {
      width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
      strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
      style: { flexShrink: 0 } as React.CSSProperties,
    };
    switch (type) {
      case 'video':
        return <svg {...common}><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>;
      case 'image':
        return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
      case 'audio':
        return <svg {...common}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>;
      default:
        return null;
    }
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-3)',
    margin: '0 0 0.5rem',
    letterSpacing: '0.02em',
  };

  return (
    <div style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)', boxShadow: '0 2px 12px rgba(0,0,0,0.07), 0 0 1px rgba(0,0,0,0.08)' }}>
      <div
        role="button"
        tabIndex={0}
        style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: collapsed ? '0' : '0.75rem', color: 'var(--text-1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={onToggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleCollapsed();
          }
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {t('review.panelTitle')}
          {collapsed && allActive && (
            <span style={{ fontSize: '0.8rem', color: 'var(--success)', fontWeight: 400 }}>
              {t('review.allPassed', { count: totalCount })}
            </span>
          )}
          {showAsset && (
            <span style={{ padding: '0.2rem 0.6rem', background: 'rgba(245,158,11,0.08)', borderRadius: '4px', border: '1px solid rgba(245,158,11,0.25)', fontSize: '0.75rem', color: '#d97706', fontWeight: 400, whiteSpace: 'nowrap' }}>
              {t('review.noFaceWarning')}
            </span>
          )}
        </span>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-3)', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {!collapsed && (
        <>
          {showAsset && (
            <div style={{ marginBottom: showResource ? '1rem' : 0 }}>
              <div style={sectionLabelStyle}>{t('review.sectionCharacters')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                {assetReviews.map((review, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.5rem', background: 'var(--surface)', borderRadius: '6px', boxShadow: '0 1px 6px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.06)', minWidth: 0 }}>
                    {review.imageUrl && (
                      <img
                        src={tosThumbnail(review.imageUrl, 36)}
                        alt={review.characterName}
                        style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {review.characterName}
                        {review.characterState && <span style={{ color: 'var(--text-3)', marginLeft: 3 }}>({review.characterState})</span>}
                      </div>
                      {review.errorMsg && <div style={{ fontSize: '0.7rem', color: 'var(--danger)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{review.errorMsg}</div>}
                    </div>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontWeight: 500,
                      flexShrink: 0,
                      background: review.status === 'active' ? 'rgba(34,197,94,0.1)' :
                                 review.status === 'processing' ? 'rgba(59,130,246,0.1)' :
                                 review.status === 'failed' ? 'rgba(239,68,68,0.1)' : 'rgba(156,163,175,0.1)',
                      color: review.status === 'active' ? 'var(--success)' :
                             review.status === 'processing' ? 'var(--primary)' :
                             review.status === 'failed' ? 'var(--danger)' : 'var(--text-3)',
                    }}>
                      {review.status === 'active' ? t('review.statusActive') :
                       review.status === 'processing' ? t('review.statusProcessing') :
                       review.status === 'failed' ? t('review.statusFailed') :
                       review.status === 'pending' ? t('review.statusPending') : t('review.statusDefault')}
                    </span>
                  </div>
                ))}
              </div>
              {assetReviews.some(review => review.status === 'failed') && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--danger)' }}>
                  {t('review.failedHint')}
                </div>
              )}
            </div>
          )}

          {showResource && (
            <div style={{ background: 'var(--surface)', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.06)', padding: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={sectionLabelStyle}>
                  {t('review.sectionResources')}
                  <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-3)', marginLeft: 6 }}>{tc('resourceReview.panelHint')}</span>
                </span>
                <button
                  onClick={onSubmitResource}
                  disabled={submittingResource}
                  style={{
                    fontSize: '0.78rem', fontWeight: 600, padding: '4px 14px', borderRadius: 6,
                    border: 'none', background: 'var(--theme-storyboard)', color: '#fff',
                    cursor: submittingResource ? 'not-allowed' : 'pointer',
                    opacity: submittingResource ? 0.6 : 1,
                    boxShadow: '0 1px 4px color-mix(in srgb, var(--theme-storyboard) 40%, transparent)',
                  }}
                >
                  {submittingResource ? tc('resourceReview.submitting') : tc('resourceReview.submit')}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                {resourceReviews.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.6rem', background: 'var(--surface)', borderRadius: '6px', boxShadow: '0 1px 6px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.06)', minWidth: 0 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name || r.fileName}>
                      <span style={{ color: 'var(--text-3)' }}>{tc('resourceReview.colShot')}#{r.shotIndex}</span>
                      <span style={{ margin: '0 5px', color: 'var(--text-3)' }}>·</span>
                      {r.name || r.fileName}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: '0.76rem', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}>
                      {resourceTypeIcon(r.resourceType)}{resourceTypeLabel(r.resourceType)}
                    </span>
                    <span style={{ flexShrink: 0, maxWidth: '42%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {resourceStatusBadge(r.status, r.errorMsg)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-3)' }}>
                {tc('resourceReview.summaryApproved')}: {resourceActiveCount} │ {tc('resourceReview.summaryProcessing')}: {resourceProcessingCount} │ {tc('resourceReview.summaryFailed')}: {resourceFailedCount} │ {tc('resourceReview.summaryUnsubmitted')}: {resourceUnsubmittedCount}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
